// "Share this register" dialog — checks if the entered email already has
// portal access; if not, offers to invite them (new login + temp password).
// Also handles the copy-link action for a user who already has access.
import { useState } from "react";
import { X, Link2, Copy, Check, Loader2, UserPlus, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const ACCENT = "#C8632A";

export function ShareRegisterDialog({ clientId, canInviteClientAdmin, shareUrl, onClose }) {
    const [email, setEmail] = useState("");
    const [checked, setChecked] = useState(null);   // {has_access, name, role} | null
    const [checking, setChecking] = useState(false);
    const [name, setName] = useState("");
    const [role, setRole] = useState("site_manager");
    const [inviting, setInviting] = useState(false);
    const [result, setResult] = useState(null);      // {temp_password, email, ...}
    const [copied, setCopied] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    async function check() {
        const e = email.trim().toLowerCase();
        if (!e) return;
        setChecking(true);
        setResult(null);
        try {
            const { data } = await api.get("/asbestos/share/check", { params: { client_id: clientId, email: e } });
            setChecked(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not check access");
        } finally {
            setChecking(false);
        }
    }

    async function sendInvite() {
        if (!name.trim() || !email.trim()) { toast.error("Name and email are required"); return; }
        setInviting(true);
        try {
            const { data } = await api.post("/asbestos/share/invite", {
                client_id: clientId, name: name.trim(), email: email.trim().toLowerCase(), role,
            });
            setResult(data);
            toast.success("Invited — share the link and temporary password below");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not invite this contact");
        } finally {
            setInviting(false);
        }
    }

    function copyLink() {
        navigator.clipboard.writeText(shareUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        });
    }

    function copyDirectLink() {
        navigator.clipboard.writeText(shareUrl).then(() => {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 1800);
        });
    }

    const roleOptions = canInviteClientAdmin
        ? [["client_admin", "Client Admin"], ["site_manager", "Site Manager"], ["reviewer", "Reviewer"]]
        : [["site_manager", "Site Manager"], ["reviewer", "Reviewer"]];

    return (
        <div className="fixed inset-0 z-[85] grid place-items-center p-4" style={{ background: "rgba(20,18,16,0.45)" }} onClick={onClose}>
            <div data-testid="asbestos-share-dialog" className="bg-white rounded-[14px] w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3.5 flex items-center justify-between border-b" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="text-[14px] font-semibold text-[#1F1F1F] flex items-center gap-2"><Link2 className="w-4 h-4" style={{ color: ACCENT }} /> Share this register</h3>
                    <button data-testid="asbestos-share-close" onClick={onClose}><X className="w-4 h-4" style={{ color: "#9A968F" }} /></button>
                </div>
                <div className="p-4 space-y-3">
                    <div data-testid="asbestos-share-direct-link" className="rounded-[10px] p-3 space-y-1.5" style={{ background: "#F6F4F0" }}>
                        <div className="text-[10px] uppercase font-bold tracking-wide" style={{ color: "#9A968F" }}>Direct link to this register</div>
                        <p className="text-[11px]" style={{ color: "#6F6A64" }}>
                            Anyone can be sent this link — they'll be asked to log in before they can view it, whether or not they have an account yet.
                        </p>
                        <div className="flex items-center gap-2">
                            <input readOnly data-testid="asbestos-share-direct-link-input" value={shareUrl}
                                className="flex-1 h-8 px-2 rounded-md border text-[11.5px] bg-white" style={{ borderColor: "#E2DDD5" }} />
                            <button data-testid="asbestos-share-direct-link-copy" onClick={copyDirectLink}
                                className="h-8 px-2.5 rounded-md text-[11.5px] font-semibold text-white inline-flex items-center gap-1 shrink-0" style={{ background: ACCENT }}>
                                {linkCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {linkCopied ? "Copied" : "Copy link"}
                            </button>
                        </div>
                    </div>

                    <hr style={{ borderColor: "#F0EDE8" }} />

                    <p className="text-[12px]" style={{ color: "#6F6A64" }}>
                        Or check a specific contact's email — if they already have portal access we'll confirm it,
                        and if not you can invite them so they get a login.
                    </p>
                    <div className="flex gap-2">
                        <input data-testid="asbestos-share-email" value={email} onChange={(e) => { setEmail(e.target.value); setChecked(null); setResult(null); }}
                            placeholder="name@client.co.uk" type="email"
                            className="flex-1 h-9 px-3 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                        <button data-testid="asbestos-share-check-btn" disabled={checking || !email.trim()} onClick={check}
                            className="h-9 px-3 rounded-lg text-[12.5px] font-semibold text-white disabled:opacity-50" style={{ background: ACCENT }}>
                            {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Check"}
                        </button>
                    </div>

                    {checked?.has_access && (
                        <div data-testid="asbestos-share-has-access" className="rounded-[10px] p-3 space-y-2" style={{ background: "#F1F8F1" }}>
                            <div className="text-[12.5px] font-semibold" style={{ color: "#2E7D4F" }}>
                                {checked.name} already has portal access ({checked.role.replace("_", " ")}).
                            </div>
                            <div className="flex items-center gap-2">
                                <input readOnly value={shareUrl} className="flex-1 h-8 px-2 rounded-md border text-[11.5px] bg-white" style={{ borderColor: "#D6E8D6" }} />
                                <button data-testid="asbestos-share-copy" onClick={copyLink}
                                    className="h-8 px-2.5 rounded-md text-[11.5px] font-semibold text-white inline-flex items-center gap-1" style={{ background: "#2E7D4F" }}>
                                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy link"}
                                </button>
                            </div>
                        </div>
                    )}

                    {checked && !checked.has_access && !result && (
                        <div data-testid="asbestos-share-invite-form" className="rounded-[10px] border p-3 space-y-2" style={{ borderColor: "#E2DDD5" }}>
                            <div className="text-[12px] font-semibold inline-flex items-center gap-1.5" style={{ color: "#1F1F1F" }}>
                                <UserPlus className="w-3.5 h-3.5" style={{ color: ACCENT }} /> No account yet — invite them
                            </div>
                            <input data-testid="asbestos-share-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name"
                                className="w-full h-8 px-2.5 rounded-md border text-[12.5px]" style={{ borderColor: "#E2DDD5" }} />
                            <select data-testid="asbestos-share-role" value={role} onChange={(e) => setRole(e.target.value)}
                                className="w-full h-8 px-2.5 rounded-md border text-[12.5px] bg-white" style={{ borderColor: "#E2DDD5" }}>
                                {roleOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            <button data-testid="asbestos-share-invite-btn" disabled={inviting} onClick={sendInvite}
                                className="w-full h-9 rounded-md text-[12.5px] font-semibold text-white disabled:opacity-60 inline-flex items-center justify-center gap-1.5" style={{ background: ACCENT }}>
                                {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Invite &amp; send link
                            </button>
                        </div>
                    )}

                    {result && (
                        <div data-testid="asbestos-share-result" className="rounded-[10px] p-3 space-y-2" style={{ background: "#FBEDE6" }}>
                            <div className="text-[12.5px] font-semibold" style={{ color: ACCENT }}>Invited {result.name} — share these once:</div>
                            <div>
                                <div className="text-[10px] uppercase font-bold tracking-wide" style={{ color: "#9A968F" }}>Link</div>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <input data-testid="asbestos-share-result-link" readOnly value={shareUrl} className="flex-1 h-8 px-2 rounded-md border text-[11.5px] bg-white" style={{ borderColor: "#E7B79C" }} />
                                    <button data-testid="asbestos-share-result-copy-link" onClick={copyLink} className="h-8 px-2 rounded-md border bg-white"><Copy className="w-3.5 h-3.5" style={{ color: ACCENT }} /></button>
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase font-bold tracking-wide" style={{ color: "#9A968F" }}>Temporary password</div>
                                <div data-testid="asbestos-share-result-password" className="mt-0.5 font-mono text-[13px] px-2 py-1.5 rounded-md bg-white border" style={{ borderColor: "#E7B79C" }}>{result.temp_password}</div>
                            </div>
                            <div className="text-[11px]" style={{ color: "#8A6A3A" }}>
                                {result.email_status === "queued_stub" ? "Email delivery isn't configured yet — please share these manually." : "A welcome email with these details has also been sent."}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
